import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, GridItem, Typography } from '@drone-js/core';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { ElementEventIds } from '@agoda/analytics-messages-definition-activities';
import { getPaxEventIds } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxEventsMapping';
import styled from 'styled-components';
import { calculateMinus, calculatePlus, PaxCounterProps } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxUtils';
import { ExperimentId } from 'core/type/ExperimentId';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';

const HiddenLegend = styled.legend`
    max-height: 0px;
    overflow: hidden;
`;

interface Props extends PaxCounterProps {
    inProgress: boolean;
    isTotalReachedMaxPax: boolean;
    onValueChange: (value: number, eventIds: ElementEventIds) => void;
}

const PaxCounterComponent: React.FC<Props> = React.memo((props) => {
    const { paxType, amount, paxReqMinPax, minPaxWhenSelected, paxIncrementalStep, paxReqMaxPax, onValueChange, title, inProgress, isTotalReachedMaxPax } = props;
    const cms = useSelector(selectCmsFromBootstrap);
    const isCheckAvailableQuantity = useExperimentIsB(ExperimentId.ACT_4598);
    const eventIds = getPaxEventIds(paxType);
    const minusElementName = `activities-${paxType.toLowerCase()}-counter-minus`;
    const plusElementName = `activities-${paxType.toLowerCase()}-counter-plus`;
    const allowIncrease = amount < paxReqMaxPax;
    const allowDecrease = amount > paxReqMinPax;
    const disableMinus = paxReqMinPax >= amount || inProgress;
    const disablePlus = paxReqMaxPax <= amount || inProgress || (isCheckAvailableQuantity && isTotalReachedMaxPax);

    const onPlus = () => {
        const calculatedValue = calculatePlus(amount, minPaxWhenSelected, paxIncrementalStep);
        allowIncrease && onValueChange(calculatedValue, eventIds.plusEventIds);
    };
    const onMinus = () => {
        const calculatedValue = calculateMinus(amount, paxReqMinPax, minPaxWhenSelected, paxIncrementalStep);
        allowDecrease && onValueChange(calculatedValue, eventIds.minusEventIds);
    };

    return (
        <GridItem sm={12}>
            <Box
                data-testid={`${paxType}-traveler-counter`}
                display="flex"
                justifyContent="space-between"
                flexDirection="row"
                alignItems="center"
                py="8"
                px="0"
                as="fieldset"
                borderWidth="0"
            >
                <HiddenLegend>{title}</HiddenLegend>
                <Typography variant="body-default-strong">{title}</Typography>
                <Box display="flex">
                    <Button
                        variant="icon"
                        hierarchy="secondary"
                        size="s"
                        data-element-name={minusElementName}
                        data-testid={minusElementName}
                        state={disableMinus ? 'disabled' : 'enabled'}
                        onClick={onMinus}
                        leadingIcon="fill.symbol.minus"
                        aria-label={`${cms[174885]} ${title}`}
                    />
                    <Box display="flex" minWidth="3em" justifyContent="center" alignItems="center" mx="8" role="status">
                        <Typography tag="span" variant="heading-l">
                            {amount}
                        </Typography>
                    </Box>
                    <Button
                        variant="icon"
                        hierarchy="secondary"
                        size="s"
                        data-element-name={plusElementName}
                        data-testid={plusElementName}
                        state={disablePlus ? 'disabled' : 'enabled'}
                        onClick={onPlus}
                        leadingIcon="fill.symbol.plus"
                        aria-label={`${cms[174884]} ${title}`}
                    />
                </Box>
            </Box>
        </GridItem>
    );
});
PaxCounterComponent.displayName = 'PaxCounter';
const PaxCounter = withErrorBoundary(PaxCounterComponent);
export { PaxCounter };
